/**
 * Client-side invoice PDF generation — new line_items shape.
 *
 * Reads the line_items + customer + job + tax + deposit shape produced by
 * the FinalizeRequest payload (and persisted on invoice.final_extraction).
 * Renders a tradesperson-style invoice on US Letter:
 *
 *   Header band      | logo (left), business + tagline | INVOICE # + dates (right)
 *   3-col strip      | CONTACT | ADDRESS | LICENSE
 *   Bands            | BILL TO (customer) | JOB SITE
 *   Items table      | DESCRIPTION | QTY | RATE | AMOUNT (flat, no category bands)
 *   Totals           | subtotal, optional discount/pre-tax/tax/deposit, BALANCE DUE
 *   PAYMENT          | profile.payment_methods (optional)
 *   NOTES            | invoice.customer_visible_notes || profile.invoice_footer_notes
 *   Page footer      | "Generated by HandInvoice" + optional "See page 2"
 *
 * Page 2 (terms / warranty / signature lines) renders only when
 * profile.terms.enabled is true and the block has content. Phase 1 always
 * defaults to disabled — the page-2 renderer exists so future content
 * doesn't require a generator rev.
 *
 * Continuation pages use the literal label "(continued)" on the right —
 * no dynamic "PAGE N OF M" math (deferred). The terms page always uses
 * the literal "PAGE 2 OF 2".
 *
 * jspdf + jspdf-autotable are dynamically imported so they don't bloat the
 * main bundle — they only fetch when a user actually triggers a download.
 */

import { formatPhone } from "./phoneFormat";
import { formatInvoiceDate } from "./dateFormat";

// US Letter @ pt: 612 x 792. 0.75in = 54pt margins.
const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN_X = 54;
const MARGIN_TOP = 54;
const MARGIN_BOTTOM = 54;
const CONTENT_W = PAGE_W - MARGIN_X * 2;

// Vertical budget: blocks that would push past this y break to a
// continuation page instead. ~42pt of space below this for the page
// footer ("Generated by HandInvoice" sits at y=770) — matches the
// spec's "break content past y=750" rule.
const PAGE_OVERFLOW_Y = PAGE_H - 42;

const COLOR_INK = [26, 25, 23];
const COLOR_GREY = [85, 85, 85];
const COLOR_FAINT = [136, 136, 136];
const COLOR_RULE = [212, 208, 197];
// Negative-amount (discount) lines render in a muted clay-red.
const COLOR_DISCOUNT = [148, 70, 60];

function _fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${abs.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(abs) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function _fmtDate(iso, locale = "en-US") {
  // Date-only-aware formatter lives in utils/dateFormat.js (tested). Falls back
  // to "now" when no date is supplied (matches the prior behavior).
  return formatInvoiceDate(iso || new Date().toISOString(), { month: "long", locale });
}

export function safeInvoiceFilename(invoiceNumber, clientName) {
  const slug = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "client";
  const num = String(invoiceNumber || "0001").replace(/#/g, "").trim();
  return `invoice-${slug(num)}-${slug(clientName)}.pdf`;
}

/**
 * Logo slot sizing. Square-ish logos use an 80×80 slot. Wide logos (aspect
 * > 1.6:1) get a 140×60 banner slot. The image is centered within the slot
 * preserving aspect ratio so the surrounding layout stays predictable.
 */
function _logoSlot(logo) {
  const w = Number(logo.width) || 1;
  const h = Number(logo.height) || 1;
  const aspect = w / h;
  const slotW = aspect > 1.6 ? 140 : 80;
  const slotH = aspect > 1.6 ? 60 : 80;
  let renderW;
  let renderH;
  if (aspect > slotW / slotH) {
    renderW = slotW;
    renderH = slotW / aspect;
  } else {
    renderH = slotH;
    renderW = slotH * aspect;
  }
  return { slotW, slotH, renderW, renderH };
}

function _drawLogo(doc, logo, x, y) {
  const { slotW, slotH, renderW, renderH } = _logoSlot(logo);
  const dx = x + (slotW - renderW) / 2;
  const dy = y + (slotH - renderH) / 2;
  const fmt = (logo.mime_type || "image/png").toLowerCase() === "image/png"
    ? "PNG"
    : "JPEG";
  // jspdf accepts raw base64 (no "data:" prefix) when format is specified.
  doc.addImage(logo.data, fmt, dx, dy, renderW, renderH);
  return { slotW, slotH };
}

function _sortedLineItems(items) {
  if (!Array.isArray(items)) return [];
  return [...items].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
}

/** "Due date: <date>" for the right side of the totals area. The backend has
 *  already applied the payment terms when computing meta.due_date, so we just
 *  show the resulting date — no "(Net N)" suffix. */
function _formatDueBy(meta, locale) {
  if (!meta?.due_date) return "";
  return `Due date: ${_fmtDate(meta.due_date, locale)}`;
}

/** Minimal continuation-page header drawn on every page after the first. */
function _drawContinuationHeader(doc, businessName) {
  const y = MARGIN_TOP;
  doc.setFont("times", "italic");
  doc.setFontSize(14);
  doc.setTextColor(...COLOR_INK);
  doc.text(businessName || "", MARGIN_X, y + 14);

  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_FAINT);
  doc.text("(continued)", PAGE_W - MARGIN_X, y + 14, { align: "right" });

  doc.setDrawColor(...COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, y + 22, PAGE_W - MARGIN_X, y + 22);
  return y + 32;
}

function _drawPageFooter(doc, hasTermsHint) {
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_FAINT);
  if (hasTermsHint) {
    doc.text(
      "See page 2 for terms and warranty",
      PAGE_W / 2,
      PAGE_H - 34,
      { align: "center" },
    );
  }
  doc.text(
    "Generated by HandInvoice",
    PAGE_W / 2,
    PAGE_H - 22,
    { align: "center" },
  );
}

/** Break to a continuation page if the next block would clip the footer. */
function _ensureRoom(doc, cursorY, neededH, businessName) {
  if (cursorY + neededH <= PAGE_OVERFLOW_Y) return cursorY;
  doc.addPage();
  return _drawContinuationHeader(doc, businessName);
}

/* ========================================================================== */
/*  Header band                                                                */
/* ========================================================================== */

function _drawHeaderBand({
  doc,
  profile,
  meta,
  invoice,
  locale,
}) {
  const p = profile;
  const m = meta;
  const inv = invoice;
  const biz = (p.business_name || "Your Business").trim();
  const tagline = (p.tagline || "").trim();
  const invNum = String(m.invoice_number || inv.invoice_number || "0001")
    .replace(/#/g, "")
    .trim();
  const issuedDate = _fmtDate(m.created_at || new Date().toISOString(), locale);
  const serviceDateRaw = m.service_date || inv.service_date;
  const serviceDate = serviceDateRaw ? _fmtDate(serviceDateRaw, locale) : "";
  const showServiceDate = serviceDate && serviceDate !== issuedDate;

  const headerTop = MARGIN_TOP;
  let leftCursor = MARGIN_X;
  let logoSlotH = 0;
  if (p.logo && p.logo.data) {
    try {
      const slot = _drawLogo(doc, p.logo, MARGIN_X, headerTop);
      leftCursor = MARGIN_X + slot.slotW + 14;
      logoSlotH = slot.slotH;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[PDF] Failed to render logo, skipping:", err);
    }
  }

  doc.setFont("times", "italic");
  doc.setFontSize(22);
  doc.setTextColor(...COLOR_INK);
  doc.text(biz, leftCursor, headerTop + 22);

  if (tagline) {
    doc.setFont("times", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_GREY);
    doc.text(tagline, leftCursor, headerTop + 36);
  }

  const rightX = PAGE_W - MARGIN_X;
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_FAINT);
  doc.text("INVOICE", rightX, headerTop + 10, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...COLOR_INK);
  doc.text(`#${invNum}`, rightX, headerTop + 28, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_GREY);
  doc.text(`Issued ${issuedDate}`, rightX, headerTop + 44, { align: "right" });
  if (showServiceDate) {
    doc.text(`Service ${serviceDate}`, rightX, headerTop + 58, { align: "right" });
  }

  const textBlockBottom = headerTop + (tagline ? 42 : 28);
  const logoBottom = logoSlotH > 0 ? headerTop + logoSlotH : 0;
  const ruleY = Math.max(textBlockBottom, logoBottom, headerTop + 60) + 6;
  doc.setDrawColor(...COLOR_INK);
  doc.setLineWidth(1.5);
  doc.line(MARGIN_X, ruleY, PAGE_W - MARGIN_X, ruleY);
  return ruleY;
}

/* ========================================================================== */
/*  Three-column contact strip — CONTACT | ADDRESS | LICENSE                  */
/* ========================================================================== */

function _drawContactStrip({ doc, profile, invoice, cursorY }) {
  const p = profile;
  const inv = invoice;
  const colGap = 16;
  const colW = (CONTENT_W - colGap * 2) / 3;
  const labelY = cursorY + 16;
  const contentY = labelY + 13;

  const contactName = (p.contractor_name || "").trim();
  const contactPhone = formatPhone(p.phone);
  const contactEmail = (p.email || "").trim();
  const address = (p.address || "").trim();
  const license = (p.license_number || "").trim();
  const po = inv?.job?.po_number ? String(inv.job.po_number).trim() : "";

  const cols = [
    {
      label: "CONTACT",
      x: MARGIN_X,
      lines: [
        contactName ? { text: contactName, bold: true } : null,
        contactPhone ? { text: contactPhone, bold: false } : null,
        contactEmail ? { text: contactEmail, bold: false } : null,
      ].filter(Boolean),
    },
    {
      label: "ADDRESS",
      x: MARGIN_X + colW + colGap,
      lines: address ? [{ text: address, bold: false, wrap: true }] : [],
    },
    {
      label: "LICENSE",
      x: MARGIN_X + (colW + colGap) * 2,
      lines: [
        license ? { text: license, bold: true } : null,
        po ? { text: `PO #: ${po}`, bold: false } : null,
      ].filter(Boolean),
    },
  ];

  // Micro labels first (mono 8pt faint). Suppress the label when the
  // column has no content — empty "ADDRESS" / "LICENSE" labels under a
  // populated CONTACT column read as a layout bug. The column position
  // still claims the grid so adjacent columns stay aligned.
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_FAINT);
  for (const col of cols) {
    if (col.lines.length === 0) continue;
    doc.text(col.label, col.x, labelY);
  }

  // Content. Track per-column y so the strip's height is the tallest column.
  let tallestY = contentY;
  for (const col of cols) {
    let lineY = contentY;
    for (const line of col.lines) {
      const text = line.wrap
        ? doc.splitTextToSize(line.text, colW)
        : line.text;
      doc.setFont("helvetica", line.bold ? "bold" : "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(...(line.bold ? COLOR_INK : COLOR_GREY));
      doc.text(text, col.x, lineY);
      const consumed = Array.isArray(text) ? text.length * 13 : 13;
      lineY += consumed;
    }
    if (lineY > tallestY) tallestY = lineY;
  }
  return tallestY + 4;
}

/* ========================================================================== */
/*  Bill-to / Job-site band — two-column                                       */
/* ========================================================================== */

function _drawBillToJobSite({ doc, invoice, cursorY }) {
  const inv = invoice;
  const customer = inv.customer || {};
  const job = inv.job || {};
  const colW = (CONTENT_W - 24) / 2;
  const leftX = MARGIN_X;
  const rightX = MARGIN_X + colW + 24;
  const labelY = cursorY + 14;
  const contentY = labelY + 16;

  // Labels
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_FAINT);
  doc.text("BILL TO", leftX, labelY);
  doc.text("JOB SITE", rightX, labelY);

  let leftY = contentY;
  let rightY = contentY;

  // BILL TO
  const customerName = (customer.name || "").trim();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...COLOR_INK);
  doc.text(customerName || "—", leftX, leftY);
  leftY += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...COLOR_GREY);
  const billLines = [];
  if (customer.company) billLines.push(customer.company);
  if (customer.address) {
    const wrapped = doc.splitTextToSize(customer.address, colW);
    billLines.push(...wrapped);
  }
  if (customer.phone) billLines.push(formatPhone(customer.phone));
  if (customer.email) billLines.push(customer.email);
  for (const line of billLines) {
    doc.text(line, leftX, leftY);
    leftY += 13;
  }

  // JOB SITE
  const siteAddress = (job.site_address || "").trim();
  if (siteAddress) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...COLOR_INK);
    const wrapped = doc.splitTextToSize(siteAddress, colW);
    doc.text(wrapped, rightX, rightY);
    rightY += wrapped.length * 13;
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(11);
    doc.setTextColor(...COLOR_FAINT);
    doc.text("Same as billing", rightX, rightY);
    rightY += 13;
  }

  const jobDescription = (job.description || "").trim();
  if (jobDescription) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_GREY);
    const wrapped = doc.splitTextToSize(jobDescription, colW);
    rightY += 4;
    doc.text(wrapped, rightX, rightY);
    rightY += wrapped.length * 12;
  }

  return Math.max(leftY, rightY) + 4;
}

/* ========================================================================== */
/*  Line items table — autoTable, grouped by category                          */
/* ========================================================================== */

function _drawLineItemsTable({ doc, autoTable, lineItems, cursorY, businessName }) {
  // Discounts (negative-amount lines) are not shown in the items table — they
  // surface in the totals area as a DISCOUNT row instead.
  const sorted = _sortedLineItems(lineItems).filter(
    (it) => Number(it.amount) >= 0,
  );
  if (sorted.length === 0) {
    // No-line edge case — fall through to a single empty body row so the
    // table head still renders. Defensive only; Phase 1 doesn't hit this.
    sorted.push({
      description: "(no line items)",
      amount: 0,
      category: null,
    });
  }

  // Flat list — no category grouping/subheaders (they were AI-guessed and
  // added clutter, e.g. an "OTHER" band). The `category` tag is still kept on
  // each line for possible future use; it just isn't printed.
  const body = [];
  for (const it of sorted) {
    const isNegative = Number(it.amount) < 0;
    const amountStyles = isNegative ? { textColor: COLOR_DISCOUNT } : {};
    const qty = it.quantity != null ? String(it.quantity) : "";
    const unit = it.unit || "";
    const qtyDisplay = qty && unit ? `${qty} ${unit}` : qty;
    const rateDisplay =
      it.unit_price != null && it.unit_price !== ""
        ? _fmtMoney(it.unit_price)
        : "";
    // First row — description, qty, rate, amount. When this line has notes,
    // suppress its bottom rule so the note row reads as part of the same
    // logical line item rather than a separate entry below.
    const firstRowDescStyles = it.notes
      ? { cellPadding: { top: 5, right: 4, bottom: 2, left: 0 } }
      : undefined;
    body.push([
      firstRowDescStyles
        ? { content: it.description, styles: firstRowDescStyles }
        : it.description,
      qtyDisplay,
      rateDisplay,
      { content: _fmtMoney(it.amount), styles: amountStyles },
    ]);
    // Notes row — italic 9.5pt grey, spans all four columns.
    if (it.notes) {
      body.push([
        {
          content: it.notes,
          colSpan: 4,
          styles: {
            font: "helvetica",
            fontStyle: "italic",
            fontSize: 9.5,
            textColor: COLOR_GREY,
            cellPadding: { top: 0, right: 4, bottom: 5, left: 0 },
            lineWidth: { bottom: 0.5 },
            lineColor: [236, 236, 234],
          },
        },
      ]);
    }
  }

  autoTable(doc, {
    startY: cursorY + 6,
    margin: { left: MARGIN_X, right: MARGIN_X, top: 80 },
    head: [["DESCRIPTION", "QTY", "RATE", "AMOUNT"]],
    body,
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 11,
      textColor: COLOR_INK,
      cellPadding: { top: 5, right: 4, bottom: 5, left: 0 },
      lineColor: [236, 236, 234],
      lineWidth: 0,
      // Notes line renders smaller via the cell text styles below.
    },
    headStyles: {
      font: "courier",
      fontStyle: "normal",
      fontSize: 8,
      textColor: COLOR_FAINT,
      lineColor: COLOR_RULE,
      lineWidth: { bottom: 0.75 },
      cellPadding: { top: 0, right: 4, bottom: 6, left: 0 },
      halign: "left",
    },
    bodyStyles: {
      lineColor: [236, 236, 234],
      lineWidth: { bottom: 0.5 },
    },
    columnStyles: {
      0: { cellWidth: CONTENT_W - 60 - 70 - 70 },
      1: { cellWidth: 60, halign: "right" },
      2: { cellWidth: 70, halign: "right" },
      3: { cellWidth: 70, halign: "right" },
    },
    didParseCell: (data) => {
      // headStyles.halign:"left" left-aligns ALL headers, but the QTY/RATE/
      // AMOUNT bodies are right-aligned — so right-align those header labels
      // too, leaving DESCRIPTION (column 0) left. Keeps headers above values.
      if (data.section === "head" && data.column.index > 0) {
        data.cell.styles.halign = "right";
      }
    },
    didDrawPage: (data) => {
      // Render the continuation header on any page after page 1 that the
      // table spills onto. autoTable bumps margin.top above to leave room.
      if (data.pageNumber > 1) {
        _drawContinuationHeader(doc, businessName);
      }
    },
  });
  return doc.lastAutoTable.finalY + 8;
}

/* ========================================================================== */
/*  Totals area — right-aligned column                                         */
/* ========================================================================== */

function _drawTotals({
  doc,
  meta,
  invoice,
  profile,
  cursorY,
  businessName,
  locale,
}) {
  const subtotal = Number(meta?.subtotal ?? 0);
  const discountTotal = Number(meta?.discount_total ?? 0);
  const hasDiscount = Number.isFinite(discountTotal) && discountTotal !== 0;
  // `subtotal` is stored NET of the discount line. The items table shows only
  // the positive lines, so SUBTOTAL here must be the gross (pre-discount) sum
  // = subtotal − discountTotal (discountTotal is non-positive). Then the
  // DISCOUNT row brings it back down: gross + discount = net.
  const grossSubtotal = +(subtotal - discountTotal).toFixed(2);
  const taxAmount = meta?.tax_amount ?? null;
  const hasTax = Number(taxAmount) > 0;
  const taxLabel = meta?.tax_label || "Tax";
  const taxValue = invoice?.tax?.value;
  const taxMode = invoice?.tax?.mode;
  const totalDue = Number(meta?.total_due ?? subtotal + (Number(taxAmount) || 0));
  const deposit = invoice?.deposit;
  const hasDeposit = deposit && Number(deposit.amount) > 0;
  const balanceDue = Number(meta?.balance_due ?? totalDue - (hasDeposit ? Number(deposit.amount) : 0));
  const dueBy = _formatDueBy(meta, locale);

  // Honest height estimate — only count rows that will actually render.
  // 14pt per right-aligned summary row + 4pt + heavy rule + 22pt balance
  // gap + 6pt + (12+4) due-by + bottom margin.
  let neededH = 14; // subtotal
  if (hasDiscount) neededH += 14; // discount row
  if (hasTax) neededH += 14;
  if (hasDeposit) neededH += 14;
  neededH += 4 + 22 + 6 + 4; // rule + BALANCE DUE row
  if (dueBy) neededH += 16;
  let y = _ensureRoom(doc, cursorY + 8, neededH, businessName);

  const rightX = PAGE_W - MARGIN_X;
  const labelX = rightX - 200;

  const drawRow = (label, amount, opts = {}) => {
    doc.setFont("courier", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...(opts.muted ? COLOR_FAINT : COLOR_GREY));
    doc.text(label, labelX, y, { align: "left" });
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(11);
    if (opts.discountTone) {
      doc.setTextColor(...COLOR_DISCOUNT);
    } else {
      doc.setTextColor(...COLOR_INK);
    }
    doc.text(_fmtMoney(amount), rightX, y, { align: "right" });
    y += 14;
  };

  drawRow("SUBTOTAL", grossSubtotal);
  if (hasDiscount) {
    drawRow("DISCOUNT", discountTotal, { discountTone: true });
  }
  if (hasTax) {
    let taxRowLabel = String(taxLabel).toUpperCase();
    if (taxMode === "percentage" && taxValue != null) {
      taxRowLabel = `${taxRowLabel} (${taxValue}%)`;
    }
    drawRow(taxRowLabel, taxAmount);
  }
  if (hasDeposit) {
    const m = deposit.method && String(deposit.method).trim();
    const depositLabel = m
      ? `DEPOSIT RECEIVED (${m.toUpperCase()})`
      : "DEPOSIT RECEIVED";
    drawRow(depositLabel, -Number(deposit.amount), { discountTone: true });
  }

  // Heavy rule above BALANCE DUE.
  y += 4;
  doc.setDrawColor(...COLOR_INK);
  doc.setLineWidth(1.5);
  doc.line(MARGIN_X, y, rightX, y);
  y += 22;

  doc.setFont("courier", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_INK);
  doc.text("BALANCE DUE", MARGIN_X, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...COLOR_INK);
  doc.text(_fmtMoney(balanceDue), rightX, y + 2, { align: "right" });
  y += 6;

  if (dueBy) {
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_GREY);
    doc.text(dueBy, rightX, y, { align: "right" });
    y += 4;
  }

  return y + 8;
}

/* ========================================================================== */
/*  Labeled text block (PAYMENT / NOTES)                                       */
/* ========================================================================== */

function _drawLabeledBlock({
  doc,
  label,
  body,
  italic,
  cursorY,
  businessName,
}) {
  if (!body || !String(body).trim()) return cursorY;
  const lineH = 12;
  // Estimate height for overflow check: rule + label + lines.
  const lines = String(body)
    .split(/\r?\n/)
    .flatMap((line) => doc.splitTextToSize(line, CONTENT_W));
  const neededH = 24 + lines.length * lineH;
  let y = _ensureRoom(doc, cursorY, neededH, businessName);

  y += 14;
  doc.setDrawColor(...COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);

  y += 13;
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_FAINT);
  doc.text(label, MARGIN_X, y);

  y += 13;
  doc.setFont("helvetica", italic ? "italic" : "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...COLOR_GREY);
  doc.text(lines, MARGIN_X, y);
  y += lines.length * lineH;

  return y + 4;
}

/* ========================================================================== */
/*  Page 2 — terms / warranty / signature lines                                */
/* ========================================================================== */

function _hasTermsContent(terms) {
  if (!terms) return false;
  if (terms.liability_text && terms.liability_text.trim()) return true;
  if (terms.warranty_matrix && terms.warranty_matrix.length > 0) return true;
  if (terms.acknowledgement_text && terms.acknowledgement_text.trim()) return true;
  if (terms.signature_lines && terms.signature_lines.length > 0) return true;
  return false;
}

function _substituteTermsTokens(text, businessName) {
  return String(text || "").replace(/\{businessName\}/g, businessName);
}

function _drawTermsPage({ doc, autoTable, profile, meta, businessName }) {
  const terms = profile.terms || {};
  doc.addPage();

  // Compact header strip: business + invoice number left, PAGE 2 OF 2 right.
  const y0 = MARGIN_TOP;
  doc.setFont("times", "italic");
  doc.setFontSize(14);
  doc.setTextColor(...COLOR_INK);
  doc.text(businessName, MARGIN_X, y0 + 14);

  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_FAINT);
  const invNum = String(meta?.invoice_number || "").replace(/#/g, "").trim();
  if (invNum) {
    doc.text(`#${invNum}`, MARGIN_X, y0 + 28);
  }
  doc.text("PAGE 2 OF 2", PAGE_W - MARGIN_X, y0 + 14, { align: "right" });

  doc.setDrawColor(...COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, y0 + 36, PAGE_W - MARGIN_X, y0 + 36);

  let cursorY = y0 + 50;

  // TERMS & CONDITIONS
  if (terms.liability_text && terms.liability_text.trim()) {
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_FAINT);
    doc.text("TERMS & CONDITIONS", MARGIN_X, cursorY);
    cursorY += 14;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_GREY);
    const body = _substituteTermsTokens(terms.liability_text, businessName);
    const lines = body
      .split(/\r?\n/)
      .flatMap((line) => doc.splitTextToSize(line, CONTENT_W));
    doc.text(lines, MARGIN_X, cursorY);
    cursorY += lines.length * 13 + 16;
  }

  // SERVICE GUARANTEE (warranty matrix)
  const wm = Array.isArray(terms.warranty_matrix) ? terms.warranty_matrix : [];
  if (wm.length > 0) {
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_FAINT);
    doc.text("SERVICE GUARANTEE", MARGIN_X, cursorY);
    cursorY += 8;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: MARGIN_X, right: MARGIN_X },
      head: [["CATEGORY", "RESIDENTIAL", "COMMERCIAL"]],
      body: wm.map((row) => [
        row.category || "",
        row.residential || "",
        row.commercial || "—",
      ]),
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: 10,
        textColor: COLOR_INK,
        cellPadding: { top: 5, right: 4, bottom: 5, left: 0 },
      },
      headStyles: {
        font: "courier",
        fontStyle: "normal",
        fontSize: 8,
        textColor: COLOR_FAINT,
        lineColor: COLOR_RULE,
        lineWidth: { bottom: 0.75 },
      },
      bodyStyles: { lineColor: [236, 236, 234], lineWidth: { bottom: 0.5 } },
    });
    cursorY = doc.lastAutoTable.finalY + 16;
  }

  // ACKNOWLEDGEMENT
  if (terms.acknowledgement_text && terms.acknowledgement_text.trim()) {
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_FAINT);
    doc.text("ACKNOWLEDGEMENT", MARGIN_X, cursorY);
    cursorY += 14;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_GREY);
    const body = _substituteTermsTokens(terms.acknowledgement_text, businessName);
    const lines = body
      .split(/\r?\n/)
      .flatMap((line) => doc.splitTextToSize(line, CONTENT_W));
    doc.text(lines, MARGIN_X, cursorY);
    cursorY += lines.length * 13 + 12;
  }

  // Signature lines
  const sigLines = Array.isArray(terms.signature_lines) ? terms.signature_lines : [];
  if (sigLines.length > 0) {
    cursorY += 24;
    const half = (CONTENT_W - 24) / 2;
    const cols = sigLines.slice(0, 2);
    cols.forEach((kind, i) => {
      const x = MARGIN_X + (half + 24) * i;
      const labelText =
        kind === "customer" ? "Customer signature" : "Contractor signature";
      // Long line for signature, then thinner line for date.
      doc.setDrawColor(...COLOR_INK);
      doc.setLineWidth(0.75);
      doc.line(x, cursorY, x + half - 60, cursorY);
      doc.line(x + half - 50, cursorY, x + half, cursorY);

      doc.setFont("courier", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLOR_FAINT);
      doc.text(labelText, x, cursorY + 10);
      doc.text("Date", x + half - 50, cursorY + 10);
    });
    cursorY += 30;
  }

  // Page-2 footer.
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_FAINT);
  doc.text(
    "Generated by HandInvoice",
    PAGE_W / 2,
    PAGE_H - 22,
    { align: "center" },
  );
}

/* ========================================================================== */
/*  Main                                                                       */
/* ========================================================================== */

/**
 * @param {{ invoice: object, profile: object|null, meta: object }} args
 * @returns {Promise<Blob>}
 */
export async function generateInvoicePdf({ invoice, profile, meta }) {
  const jspdfMod = await import("jspdf");
  const jsPDF = jspdfMod.jsPDF || jspdfMod.default;
  const autotableMod = await import("jspdf-autotable");
  const autoTable = autotableMod.autoTable || autotableMod.default;

  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const p = profile || {};
  const m = meta || {};
  const inv = invoice || {};
  const locale = inv.locale || m.locale || p.locale || "en-US";
  const biz = (p.business_name || "Your Business").trim();

  const termsEnabled = !!(p.terms && p.terms.enabled && _hasTermsContent(p.terms));

  // Page 1 layout
  let cursorY = _drawHeaderBand({ doc, profile: p, meta: m, invoice: inv, locale });
  cursorY = _drawContactStrip({ doc, profile: p, invoice: inv, cursorY });
  cursorY = _drawBillToJobSite({ doc, invoice: inv, cursorY });
  cursorY = _drawLineItemsTable({
    doc,
    autoTable,
    lineItems: inv.line_items,
    cursorY,
    businessName: biz,
  });
  cursorY = _drawTotals({
    doc,
    meta: m,
    invoice: inv,
    profile: p,
    cursorY,
    businessName: biz,
    locale,
  });
  cursorY = _drawLabeledBlock({
    doc,
    label: "PAYMENT",
    body: p.payment_methods,
    italic: false,
    cursorY,
    businessName: biz,
  });
  // Per-invoice override semantics:
  //   null/undefined → inherit profile.invoice_footer_notes
  //   ""             → explicit suppression (no NOTES block)
  //   "text"         → use the override verbatim
  // _drawLabeledBlock skips when body is empty, so either an empty string
  // (explicit suppression) or null fallback chains transparently.
  const notesOverridden =
    inv.customer_visible_notes !== null && inv.customer_visible_notes !== undefined;
  const notesBody = notesOverridden
    ? String(inv.customer_visible_notes)
    : (p.invoice_footer_notes || "");
  cursorY = _drawLabeledBlock({
    doc,
    label: "NOTES",
    body: notesBody,
    italic: true,
    cursorY,
    businessName: biz,
  });

  _drawPageFooter(doc, termsEnabled);

  // Page 2 — terms / warranty / signatures.
  if (termsEnabled) {
    _drawTermsPage({ doc, autoTable, profile: p, meta: m, businessName: biz });
  }

  return doc.output("blob");
}
